import React, { useState, useEffect } from 'react';

/**
 * Banner para invitar a instalar la app nativa
 * - Solo se muestra en navegadores móviles (no en apps nativas ni PWA instalada)
 * - Detecta plataforma (iOS/Android) y muestra el link correspondiente
 * - Animación suave desde abajo (snackbar style)
 * - Se puede cerrar permanentemente (localStorage)
 * - Aparece después de 5 segundos de navegación
 */
const InstallAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Detectar si debe mostrarse el banner
    const shouldShowBanner = () => {
      // 1. No mostrar en app nativa (Capacitor)
      const isNative = window.Capacitor?.isNativePlatform?.() || false;
      if (isNative) {
        console.log('🚫 InstallAppBanner: App nativa detectada, no se muestra banner');
        return false;
      }

      // 2. No mostrar en PWA instalada
      const isPWA = window.matchMedia('(display-mode: standalone)').matches;
      if (isPWA) {
        console.log('🚫 InstallAppBanner: PWA instalada detectada, no se muestra banner');
        return false;
      }

      // 3. No mostrar en desktop
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (!isMobile) {
        console.log('🚫 InstallAppBanner: Desktop detectado, no se muestra banner');
        return false;
      }

      // 4. Verificar si el usuario cerró el banner permanentemente
      const bannerDismissed = localStorage.getItem('installAppBannerDismissed');
      if (bannerDismissed === 'true') {
        console.log('🚫 InstallAppBanner: Usuario cerró el banner permanentemente');
        return false;
      }

      // 5. Verificar última vez que se mostró (máximo 1 vez cada 3 días)
      const lastShown = localStorage.getItem('installAppBannerLastShown');
      if (lastShown) {
        const daysSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
        if (daysSinceLastShown < 3) {
          console.log(`🚫 InstallAppBanner: Se mostró hace ${daysSinceLastShown.toFixed(1)} días, esperar 3 días`);
          return false;
        }
      }

      // 6. En iOS Safari, no mostrar si ya tiene el Smart App Banner nativo
      const isIOSSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) && 
                          /Safari/.test(navigator.userAgent) && 
                          !/CriOS|FxiOS|OPiOS|mercury/i.test(navigator.userAgent);
      if (isIOSSafari) {
        console.log('🚫 InstallAppBanner: iOS Safari detectado, usar Smart App Banner nativo');
        return false;
      }

      console.log('✅ InstallAppBanner: Mostrando banner de instalación');
      return true;
    };

    // Timer para mostrar el banner después de 5 segundos
    const timer = setTimeout(() => {
      if (shouldShowBanner()) {
        setShowBanner(true);
        // Guardar timestamp de cuando se mostró
        localStorage.setItem('installAppBannerLastShown', Date.now().toString());
        // Animar la entrada
        setTimeout(() => setIsVisible(true), 100);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Función para detectar plataforma y obtener link de descarga
  const getDownloadLink = () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      return {
        platform: 'iOS',
        icon: '📱',
        store: 'App Store',
        url: 'https://apps.apple.com/es/app/agustinos/id1560369081'
      };
    } else if (isAndroid) {
      return {
        platform: 'Android',
        icon: '📱',
        store: 'Play Store',
        url: 'https://play.google.com/store/apps/details?id=com.appagustinos.agustinos'
      };
    } else {
      // Fallback genérico
      return {
        platform: 'Mobile',
        icon: '📱',
        store: 'la tienda de apps',
        url: '#'
      };
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleDismissForever = () => {
    localStorage.setItem('installAppBannerDismissed', 'true');
    handleClose();
    console.log('👋 InstallAppBanner: Usuario cerró el banner permanentemente');
  };

  const handleDownload = () => {
    const downloadInfo = getDownloadLink();
    console.log(`📱 InstallAppBanner: Redirigiendo a ${downloadInfo.store}`);
    window.open(downloadInfo.url, '_blank');
    handleClose();
  };

  if (!showBanner) return null;

  const downloadInfo = getDownloadLink();

  return (
    <>
      {/* Overlay transparente para cerrar al tocar fuera */}
      <div 
        className={`fixed inset-0 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ zIndex: 1050 }}
        onClick={handleClose}
      />

      {/* Banner desde abajo - Por encima del BottomNavigation */}
      <div 
        className={`fixed bottom-0 left-0 right-0 transition-all duration-300 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{
          zIndex: 1100,
          paddingBottom: 'calc(max(env(safe-area-inset-bottom), 16px) + 80px)'
        }}
      >
        <div 
          className="mx-4 mb-4 bg-white rounded-2xl shadow-2xl border border-gray-200"
          style={{
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          {/* Contenido del banner */}
          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* Icono de la app */}
              <div className="flex-shrink-0">
                <img 
                  src="/logo192.png" 
                  alt="Agustinos" 
                  className="w-12 h-12 rounded-xl"
                  style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
                />
              </div>

              {/* Texto */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {downloadInfo.icon} ¿Prefieres la app nativa?
                </h3>
                <p className="text-sm text-gray-600 leading-snug">
                  Mejor experiencia, más rápida y siempre actualizada
                </p>
              </div>

              {/* Botón cerrar */}
              <button
                onClick={handleClose}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Cerrar"
              >
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>
            </div>

            {/* Botones de acción */}
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleDownload}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200"
                style={{ 
                  background: 'linear-gradient(135deg, #c82318 0%, #a01916 100%)',
                  boxShadow: '0 2px 8px rgba(200, 35, 24, 0.3)'
                }}
              >
                Abrir en {downloadInfo.store}
              </button>
              
              <button
                onClick={handleDismissForever}
                className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                No mostrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstallAppBanner;

